import { useCallback, useMemo, useState } from "react";

import { Description, Edit, Person, Save, Cancel } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  BulletinTypeResource,
  createBulletinTypeBypass,
  deleteBulletinTypeBypass,
  getBulletinTypes,
  getGroupMemberPermissions,
  getGroupMembers,
  updateGroupMemberPermissions,
  UserResource,
} from "@rubi/api-generated";
import { useTranslation } from "react-i18next";

import { BulletinTypeBypass, BYPASS_RULES_LABELS, UserBypass } from "./types";

interface BypassRulesSectionProps {
  groupId: number;
  userBypasses: UserBypass[];
  bulletinTypeBypasses: BulletinTypeBypass[];
  canEdit: boolean;
  onUpdate: () => void;
}

const BypassRulesSection = ({
  groupId,
  userBypasses,
  bulletinTypeBypasses,
  canEdit,
  onUpdate,
}: BypassRulesSectionProps) => {
  const { t } = useTranslation("bulletin/bulletinType");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  const USER_LIST_LIMIT = 5;

  // User bypass state
  const [allMembers, setAllMembers] = useState<UserResource[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserResource[]>([]);

  // Bulletin type bypass state
  const [allBulletinTypeResources, setAllBulletinTypeResources] = useState<
    BulletinTypeResource[]
  >([]);
  const [selectedBulletinTypeResources, setSelectedBulletinTypeResources] =
    useState<BulletinTypeResource[]>([]);

  // Memoize userBypassIds to prevent unnecessary re-creation of callbacks
  const userBypassIds = useMemo(
    () => userBypasses.map((u) => u.id),
    [userBypasses],
  );

  // Get display name, falling back to email if no first/last name
  const getUserDisplayName = (user: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name || user.email || "";
  };

  // Get bulletin type label using i18n translation
  const getBulletinTypeResourceLabel = (typeKey: string) => {
    return t(typeKey, { defaultValue: typeKey });
  };

  // Fetch all data when entering edit mode
  const fetchEditData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, bulletinTypesRes] = await Promise.all([
        getGroupMembers({ path: { group_id: groupId } }),
        getBulletinTypes(),
      ]);
      const members = membersRes.data!;
      const bulletinTypes = bulletinTypesRes.data!;

      setAllMembers(members);
      setAllBulletinTypeResources(bulletinTypes);

      // Pre-select users that already have bypass permission
      const preSelectedUsers = members.filter((m: UserResource) =>
        userBypassIds.includes(m.id),
      );
      setSelectedUsers(preSelectedUsers);

      // Pre-select bulletin types that already bypass moderation
      const currentBypassTypes = bulletinTypeBypasses.map(
        (b) => b.bulletin_type,
      );
      const preSelectedTypes = bulletinTypes.filter((t: BulletinTypeResource) =>
        currentBypassTypes.includes(t.reference_name),
      );
      setSelectedBulletinTypeResources(preSelectedTypes);
    } catch (error) {
      console.error("Error fetching edit data:", error);
    } finally {
      setLoading(false);
    }
  }, [groupId, userBypassIds, bulletinTypeBypasses]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    fetchEditData();
  }, [fetchEditData]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
    setSelectedUsers([]);
    setSelectedBulletinTypeResources([]);
  }, []);

  const handleSaveClick = useCallback(async () => {
    setSaving(true);
    try {
      // === Handle User Bypasses ===
      const selectedUserIds = selectedUsers.map((u) => u.id);

      // Find users to add bypass permission
      const usersToAdd = selectedUsers.filter(
        (u) => !userBypassIds.includes(u.id),
      );

      // Find users to remove bypass permission
      const usersToRemove = userBypasses.filter(
        (u) => !selectedUserIds.includes(u.id),
      );

      // Update user permissions
      for (const user of usersToAdd) {
        const currentPerms = await getGroupMemberPermissions({
          path: { group_id: groupId, user_id: user.id },
        });
        await updateGroupMemberPermissions({
          path: { group_id: groupId, user_id: user.id },
          body: {
            bypass_moderation: true,
            moderate_bulletin_admittance:
              currentPerms.data?.moderate_bulletin_admittance ?? false,
            manage_members: currentPerms.data?.manage_members ?? false,
            manage_admins: currentPerms.data?.manage_admins ?? false,
            flag_bulletin: currentPerms.data?.flag_bulletin ?? false,
            manage_group: currentPerms.data?.manage_group ?? false,
            can_share_bulletin: currentPerms.data?.can_share_bulletin ?? false,
            can_revoke_bulletin:
              currentPerms.data?.can_revoke_bulletin ?? false,
            can_promote_content_moderators:
              currentPerms.data?.can_promote_content_moderators ?? false,
            can_transfer_bulletin_to_another_group:
              currentPerms.data?.can_transfer_bulletin_to_another_group ??
              false,
            can_moderate_content:
              currentPerms.data?.can_moderate_content ?? false,
          },
        });
      }

      for (const user of usersToRemove) {
        const currentPerms = await getGroupMemberPermissions({
          path: { group_id: groupId, user_id: user.id },
        });
        await updateGroupMemberPermissions({
          path: { group_id: groupId, user_id: user.id },
          body: {
            bypass_moderation: false,
            moderate_bulletin_admittance:
              currentPerms.data?.moderate_bulletin_admittance ?? false,
            manage_members: currentPerms.data?.manage_members ?? false,
            manage_admins: currentPerms.data?.manage_admins ?? false,
            flag_bulletin: currentPerms.data?.flag_bulletin ?? false,
            manage_group: currentPerms.data?.manage_group ?? false,
            can_share_bulletin: currentPerms.data?.can_share_bulletin ?? false,
            can_revoke_bulletin:
              currentPerms.data?.can_revoke_bulletin ?? false,
            can_promote_content_moderators:
              currentPerms.data?.can_promote_content_moderators ?? false,
            can_transfer_bulletin_to_another_group:
              currentPerms.data?.can_transfer_bulletin_to_another_group ??
              false,
            can_moderate_content:
              currentPerms.data?.can_moderate_content ?? false,
          },
        });
      }

      // === Handle Bulletin Type Bypasses ===
      const selectedTypeNames = selectedBulletinTypeResources.map(
        (t) => t.reference_name,
      );
      const currentBypassTypes = bulletinTypeBypasses.map(
        (b) => b.bulletin_type,
      );

      // Find types to add
      const typesToAdd = selectedBulletinTypeResources.filter(
        (t) => !currentBypassTypes.includes(t.reference_name),
      );

      // Find types to remove (need to find the bypass record to get its ID)
      const typesToRemove = bulletinTypeBypasses.filter(
        (b) => !selectedTypeNames.includes(b.bulletin_type),
      );

      // Create new bypasses
      for (const type of typesToAdd) {
        await createBulletinTypeBypass({
          path: { group_id: groupId },
          body: { bulletin_type: type.reference_name },
        });
      }

      // Delete removed bypasses
      for (const bypass of typesToRemove) {
        await deleteBulletinTypeBypass({
          path: { group_id: groupId, bypass_id: bypass.id },
        });
      }

      setIsEditing(false);
      onUpdate(); // Refresh parent data
    } catch (error) {
      console.error("Error updating bypass rules:", error);
    } finally {
      setSaving(false);
    }
  }, [
    selectedUsers,
    selectedBulletinTypeResources,
    userBypasses,
    userBypassIds,
    bulletinTypeBypasses,
    groupId,
    onUpdate,
  ]);

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ mb: 0 }}>
          {BYPASS_RULES_LABELS.sectionTitle}
        </Typography>
        {canEdit && !isEditing && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={handleEditClick}
          >
            Edit Rules
          </Button>
        )}
        {isEditing && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Cancel />}
              onClick={handleCancelClick}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSaveClick}
              disabled={saving}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {BYPASS_RULES_LABELS.sectionDescription}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {/* Users with Bypass Permission */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {BYPASS_RULES_LABELS.usersSubtitle}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {BYPASS_RULES_LABELS.usersDescription}
            </Typography>

            {isEditing ? (
              loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={allMembers}
                  value={selectedUsers}
                  onChange={(_, newValue) => setSelectedUsers(newValue)}
                  getOptionLabel={(option) => getUserDisplayName(option)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Select users..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">
                          {getUserDisplayName(option)}
                        </Typography>
                        {(option.first_name || option.last_name) && (
                          <Typography variant="caption" color="textSecondary">
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getUserDisplayName(option)}
                        size="small"
                      />
                    ))
                  }
                  sx={{ mt: 1 }}
                />
              )
            ) : userBypasses.length > 0 ? (
              <>
                <List dense>
                  {(showAllUsers
                    ? userBypasses
                    : userBypasses.slice(0, USER_LIST_LIMIT)
                  ).map((user) => (
                    <ListItem key={user.id} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Person fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getUserDisplayName(user)}
                        secondary={
                          user.first_name || user.last_name ? user.email : null
                        }
                        primaryTypographyProps={{ variant: "body2" }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                  ))}
                </List>
                {userBypasses.length > USER_LIST_LIMIT && (
                  <Button
                    size="small"
                    onClick={() => setShowAllUsers((prev) => !prev)}
                    sx={{ mt: 0.5, textTransform: "none" }}
                  >
                    {showAllUsers
                      ? "Hide"
                      : `Show all ${userBypasses.length} users`}
                  </Button>
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: "italic", mt: 1 }}
              >
                {BYPASS_RULES_LABELS.noUsers}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Bulletin Types that Bypass Moderation */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {BYPASS_RULES_LABELS.bulletinTypesSubtitle}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {BYPASS_RULES_LABELS.bulletinTypesDescription}
            </Typography>

            {isEditing ? (
              loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={allBulletinTypeResources}
                  value={selectedBulletinTypeResources}
                  onChange={(_, newValue) =>
                    setSelectedBulletinTypeResources(newValue)
                  }
                  getOptionLabel={(option) =>
                    getBulletinTypeResourceLabel(option.reference_name)
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.reference_name === value.reference_name
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Select bulletin types..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.reference_name}>
                      <Typography variant="body2">
                        {getBulletinTypeResourceLabel(option.reference_name)}
                      </Typography>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.reference_name}
                        label={getBulletinTypeResourceLabel(
                          option.reference_name,
                        )}
                        size="small"
                      />
                    ))
                  }
                  sx={{ mt: 1 }}
                />
              )
            ) : bulletinTypeBypasses.length > 0 ? (
              <List dense>
                {bulletinTypeBypasses.map((bypass) => (
                  <ListItem key={bypass.id} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Description fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={getBulletinTypeResourceLabel(
                        bypass.bulletin_type,
                      )}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: "italic", mt: 1 }}
              >
                {BYPASS_RULES_LABELS.noBulletinTypes}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BypassRulesSection;
